import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, Camera, Mail, LogOut, ChevronDown, X, Loader2, User as UserIcon, Home as HomeIcon, ExternalLink, Bell, BellOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { changePassword, patchMyProfile, requestOwnReset, uploadFile } from '../../services/api';
import { pushSupported, getPushState, subscribePush, unsubscribePush, testPush, getPushPreferences, putPushPreferences } from '../../services/push';
import { toast } from '../../hooks/use-toast';

/**
 * ProfileMenu — Avatar + dropdown in the admin top-right corner.
 * Provides: change password · edit profile (name + photo) · reset password.
 */
const ProfileMenu = () => {
  const { user, logout, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null);  // 'password' | 'profile' | 'push_prefs' | null
  const [pushState, setPushState] = useState('default');
  const [pushBusy, setPushBusy] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (open) getPushState().then(setPushState).catch(() => setPushState('unsupported'));
  }, [open]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushState === 'subscribed') {
        await unsubscribePush();
        toast({ title: 'Browser notifications off' });
      } else {
        await subscribePush();
        toast({ title: 'Browser notifications on', description: 'You will get pings for @mentions and updates.' });
        // Fire a test so the user knows it works
        try { await testPush(); } catch { /* ignore */ }
      }
      setPushState(await getPushState());
    } catch (e) {
      toast({ title: e?.message || 'Failed', variant: 'destructive' });
    } finally { setPushBusy(false); }
  };

  const doResetPassword = async () => {
    if (!user?.email) return toast({ title: 'No email on this account', variant: 'destructive' });
    try {
      await requestOwnReset(user.email);
      toast({ title: 'Reset link sent', description: `Check ${user.email}` });
      setOpen(false);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed to send', variant: 'destructive' });
    }
  };

  const initial = (user?.name || user?.email || 'U').slice(0, 1).toUpperCase();

  return (
    <div className="relative" ref={ref} data-testid="profile-menu">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="profile-avatar-btn"
        className="flex items-center gap-2 px-1.5 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white transition"
        aria-label="Profile menu"
      >
        <div className="w-8 h-8 rounded-full bg-[#0f5c3d] flex items-center justify-center text-sm font-semibold overflow-hidden">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <span>{initial}</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-black/5 overflow-hidden z-50" data-testid="profile-dropdown">
          <div className="px-4 py-3 border-b border-black/5 flex items-center gap-3 bg-[#f7fbf8]">
            <div className="w-10 h-10 rounded-full bg-[#0f5c3d] text-white flex items-center justify-center text-sm font-semibold overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <span>{initial}</span>}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#0b1a2e] truncate">{user?.name || 'Unnamed user'}</div>
              <div className="text-[11px] text-[#0b1a2e]/60 truncate">{user?.email}</div>
              <div className="text-[9px] uppercase tracking-widest text-[#0f5c3d] mt-0.5">{user?.role}</div>
            </div>
          </div>
          <MenuItem icon={UserIcon} label="Edit profile" testId="menu-edit-profile" onClick={() => { setModal('profile'); setOpen(false); }} />
          <MenuItem icon={KeyRound} label="Change password" testId="menu-change-password" onClick={() => { setModal('password'); setOpen(false); }} />
          <MenuItem icon={Mail} label="Email me a reset link" testId="menu-reset-password" onClick={doResetPassword} />
          {pushSupported() && (
            <>
              <button
                onClick={togglePush}
                disabled={pushBusy || pushState === 'denied' || pushState === 'unsupported'}
                data-testid="menu-push-toggle"
                className="w-full text-left px-4 py-2.5 text-sm text-[#0b1a2e] hover:bg-[#f7fbf8] flex items-center gap-2 transition disabled:opacity-50">
                {pushState === 'subscribed' ? <Bell className="w-4 h-4 text-[#0f5c3d]" /> : <BellOff className="w-4 h-4 text-[#0f5c3d]" />}
                <span className="flex-1">
                  {pushState === 'subscribed' ? 'Turn off browser alerts'
                    : pushState === 'denied' ? 'Alerts blocked in browser settings'
                    : 'Enable browser alerts'}
                </span>
                {pushBusy && <Loader2 className="w-3 h-3 animate-spin" />}
              </button>
              {pushState === 'subscribed' && (
                <button onClick={() => { setModal('push_prefs'); setOpen(false); }}
                  data-testid="menu-push-prefs"
                  className="w-full text-left px-4 py-2 text-xs text-[#0b1a2e]/70 hover:bg-[#f7fbf8] flex items-center gap-2 transition pl-11">
                  Choose what to be pinged about →
                </button>
              )}
            </>
          )}
          <div className="my-1 border-t border-black/5" />
          <Link to="/" data-testid="menu-public-site" className="block px-4 py-2.5 text-sm text-[#0b1a2e] hover:bg-[#f7fbf8] flex items-center gap-2">
            <HomeIcon className="w-4 h-4" /> Public site <ExternalLink className="w-3 h-3 ml-auto opacity-60" />
          </Link>
          <button onClick={logout} data-testid="menu-signout" className="w-full text-left px-4 py-2.5 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}

      {modal === 'password' && (
        <ChangePasswordModal onClose={() => setModal(null)} />
      )}
      {modal === 'profile' && (
        <EditProfileModal onClose={() => setModal(null)} onSaved={(u) => setUser?.(u)} user={user} />
      )}
      {modal === 'push_prefs' && (
        <PushPrefsModal onClose={() => setModal(null)} />
      )}
    </div>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="w-full text-left px-4 py-2.5 text-sm text-[#0b1a2e] hover:bg-[#f7fbf8] flex items-center gap-2 transition">
    <Icon className="w-4 h-4 text-[#0f5c3d]" /> {label}
  </button>
);

const PushPrefsModal = ({ onClose }) => {
  const [prefs, setPrefs] = useState({ mentions: true, assignments: true, followups: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getPushPreferences().then(setPrefs).catch(() => {}).finally(() => setLoading(false)); }, []);
  const toggle = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));
  const save = async () => {
    setSaving(true);
    try { await putPushPreferences(prefs); toast({ title: 'Preferences saved' }); onClose(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  const items = [
    { key: 'mentions', label: '@Mentions', hint: 'When someone types @you in a task comment or message' },
    { key: 'assignments', label: 'Task & ticket assignments', hint: 'When work gets handed to you, reassigned, or claimed' },
    { key: 'followups', label: 'Follow-up nudges', hint: 'Explicit "please look at this" pings + inactivity nudges' },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="push-prefs-modal">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex items-center justify-between">
          <div>
            <div className="text-lg font-medium">Notification preferences</div>
            <div className="text-xs text-white/60">Pick what wakes your phone. Inbox items are always saved.</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="close-push-prefs"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {loading ? (
            <div className="text-center py-6 text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>
          ) : (
            <>
              {items.map((it) => (
                <label key={it.key} className="flex items-start gap-3 p-3 rounded-lg border border-black/5 hover:bg-[#f7fbf8] cursor-pointer">
                  <input type="checkbox" checked={!!prefs[it.key]} onChange={() => toggle(it.key)}
                    data-testid={`push-pref-${it.key}`}
                    className="mt-0.5 w-4 h-4 accent-[#0f5c3d]" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#0b1a2e]">{it.label}</div>
                    <div className="text-xs text-[#0b1a2e]/60 mt-0.5">{it.hint}</div>
                  </div>
                </label>
              ))}
              <button onClick={save} disabled={saving} data-testid="push-prefs-save"
                className="w-full px-4 py-2.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save preferences'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast({ title: 'New password must be at least 8 characters', variant: 'destructive' });
    if (form.new_password !== form.confirm) return toast({ title: 'Passwords do not match', variant: 'destructive' });
    setSaving(true);
    try {
      await changePassword(form.current_password, form.new_password);
      toast({ title: 'Password changed' });
      onClose();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Change failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="change-password-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex items-center justify-between">
          <div className="text-lg font-medium">Change password</div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/10 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Current password">
            <input required type="password" value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              data-testid="current-password"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </Field>
          <Field label="New password">
            <input required type="password" value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              data-testid="new-password" minLength={8}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            <div className="text-[11px] text-[#0b1a2e]/50 mt-1">Minimum 8 characters</div>
          </Field>
          <Field label="Confirm new password">
            <input required type="password" value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              data-testid="confirm-password"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </Field>
        </div>
        <div className="px-5 py-3 bg-[#f7fbf8] flex justify-end gap-2 border-t border-black/5">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="submit-change-password"
            className="px-4 py-1.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Change'}
          </button>
        </div>
      </form>
    </div>
  );
};

const EditProfileModal = ({ onClose, onSaved, user }) => {
  const [name, setName] = useState(user?.name || '');
  const [picture, setPicture] = useState(user?.picture || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast({ title: 'Max 5 MB', variant: 'destructive' });
    setUploading(true);
    try {
      const up = await uploadFile(f);
      setPicture(up.url);
    } catch { toast({ title: 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = {};
      if (name.trim() !== user?.name) patch.name = name.trim();
      if (picture !== user?.picture) patch.picture = picture;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      const updated = await patchMyProfile(patch);
      onSaved?.(updated);
      toast({ title: 'Profile updated' });
      onClose();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="edit-profile-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex items-center justify-between">
          <div className="text-lg font-medium">Edit profile</div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/10 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-col items-center gap-3">
            <div className="w-24 h-24 rounded-full bg-[#0f5c3d] text-white flex items-center justify-center text-3xl font-semibold overflow-hidden">
              {picture ? (
                <img src={picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <span>{(name || 'U').slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-sm font-medium cursor-pointer hover:bg-[#f7fbf8]">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {uploading ? 'Uploading…' : 'Change photo'}
              <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="upload-picture" />
            </label>
          </div>
          <Field label="Display name">
            <input required value={name} onChange={(e) => setName(e.target.value)}
              data-testid="profile-name"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </Field>
          <Field label="Email (read-only)">
            <input disabled value={user?.email || ''}
              className="w-full px-3 py-2 border border-slate-200 bg-slate-50 rounded text-sm text-[#0b1a2e]/60" />
          </Field>
          <Field label="Role (read-only)">
            <input disabled value={user?.role || ''}
              className="w-full px-3 py-2 border border-slate-200 bg-slate-50 rounded text-sm text-[#0b1a2e]/60" />
          </Field>
        </div>
        <div className="px-5 py-3 bg-[#f7fbf8] flex justify-end gap-2 border-t border-black/5">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="submit-profile"
            className="px-4 py-1.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-1">{label}</div>
    {children}
  </label>
);

export default ProfileMenu;
